import { useEffect, useState } from 'react';
import { getCachedNews } from '../services/newsCache';

const useCachedNewsQuery = (
  cacheKey,
  loadNews,
  fallbackData = []
) => {
  const [result, setResult] = useState(() => ({
    cacheKey,
    data: getCachedNews(cacheKey) || [],
  }));
  const [initialLoading, setInitialLoading] = useState(
    () => getCachedNews(cacheKey) === undefined
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const cachedData = getCachedNews(cacheKey);
  const hasCurrentResult = result.cacheKey === cacheKey;
  const exactNews =
    hasCurrentResult
      ? result.data
      : cachedData || [];
  const currentInitialLoading = hasCurrentResult
    ? initialLoading
    : cachedData === undefined;
  const currentIsRefreshing = hasCurrentResult
    ? isRefreshing
    : cachedData !== undefined;
  const hasFallbackData =
    currentInitialLoading
    && exactNews.length === 0
    && fallbackData.length > 0;
  const news = hasFallbackData ? fallbackData : exactNews;

  useEffect(() => {
    let alive = true;
    const existingData = getCachedNews(cacheKey);

    if (existingData !== undefined) {
      setResult({
        cacheKey,
        data: existingData,
      });
      setInitialLoading(false);
      setIsRefreshing(true);
    } else {
      setResult({
        cacheKey,
        data: [],
      });
      setInitialLoading(true);
      setIsRefreshing(false);
    }

    const fetchNews = async () => {
      try {
        setError(null);
        const data = await loadNews();

        if (alive) {
          setResult({
            cacheKey,
            data,
          });
        }
      } catch (loadError) {
        console.error('Error loading cached news:', loadError);

        if (alive) {
          setError(loadError);
        }
      } finally {
        if (alive) {
          setInitialLoading(false);
          setIsRefreshing(false);
        }
      }
    };

    fetchNews();

    return () => {
      alive = false;
    };
  }, [cacheKey, loadNews]);

  return {
    news,
    initialLoading: currentInitialLoading && !hasFallbackData,
    isRefreshing: currentIsRefreshing || hasFallbackData,
    isUsingFallback: hasFallbackData,
    exactDataReady: !currentInitialLoading,
    error,
  };
};

export default useCachedNewsQuery;
